// Reports from visitors (D-096), at the top of the Inbox until someone decides: keep the post,
// remove it, or reply to the visitor.
import type { ActionResult, Post } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, post } from "../api";
import { useWorkspace } from "./Shell";
import { useToast } from "./Toast";

interface Report {
  id: string;
  reason: string;
  reasonLabel: string;
  message: string | null;
  email: string | null;
  createdAt: string;
  repliedAt: string | null;
  post: Post | null;
}

export function Reports() {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const workspace = useWorkspace();
  const canAct = workspace.role !== "viewer";
  const q = useQuery({
    queryKey: ["reports", ws],
    queryFn: () => api<{ reports: Report[]; open: number }>(`/v1/w/${ws}/reports`),
  });
  const [replying, setReplying] = useState<string | null>(null);
  const [text, setText] = useState("");
  const reports = q.data?.reports ?? [];
  if (reports.length === 0) return null;
  // Keep and Remove decide every open report on the same post at once.
  const onPost = new Map<string, number>();
  for (const r of reports) if (r.post) onPost.set(r.post.id, (onPost.get(r.post.id) ?? 0) + 1);

  const act = async (id: string, what: "keep" | "remove" | "reply", message?: string) => {
    try {
      const r =
        what === "keep"
          ? await post<ActionResult>(`/v1/w/${ws}/reports/${id}/keep`)
          : what === "remove"
            ? await post<ActionResult>(`/v1/w/${ws}/reports/${id}/remove`)
            : await post<ActionResult>(`/v1/w/${ws}/reports/${id}/reply`, { message });
      toast(r.summary);
      setReplying(null);
      setText("");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["reports", ws] }),
        qc.invalidateQueries({ queryKey: ["posts", ws] }),
      ]);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "That didn't work. Please try again.");
    }
  };

  return (
    <section
      className="card stack reports"
      aria-label="Reports from visitors"
      style={{ marginBottom: 16 }}
    >
      <h2 style={{ margin: 0 }}>
        {reports.length === 1
          ? "A visitor reported a post"
          : `${reports.length} reports from visitors`}
      </h2>
      {reports.map((r) => (
        <div key={r.id} className="row" style={{ alignItems: "flex-start", gap: 12 }}>
          {r.post?.previewUrl ? (
            <img
              src={r.post.previewUrl}
              alt=""
              width={72}
              height={90}
              style={{ objectFit: "cover", borderRadius: 8 }}
            />
          ) : (
            <div style={{ width: 72, height: 90, borderRadius: 8, background: "#eee" }} />
          )}
          <div className="stack spacer" style={{ gap: 4 }}>
            <b>{r.reasonLabel}</b>
            <span className="small muted">
              {r.post?.creator ? `@${r.post.creator}'s post` : "A post"} ·{" "}
              {new Date(r.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
              })}
              {r.post?.hidden
                ? " · hidden from all walls until you decide"
                : " · still on your walls"}
            </span>
            {r.message && <span className="small">"{r.message}"</span>}
            {r.email && (
              <span className="small muted">
                Reply to {r.email}
                {r.repliedAt ? " (replied)" : ""}
              </span>
            )}
            {canAct && (
              <div className="row wrap" style={{ gap: 8 }}>
                <button type="button" className="btn sm" onClick={() => act(r.id, "keep")}>
                  Keep it
                </button>
                <button
                  type="button"
                  className="btn secondary sm"
                  onClick={() => act(r.id, "remove")}
                >
                  Remove it
                </button>
                {r.email && (
                  <button
                    type="button"
                    className="btn link sm"
                    onClick={() => setReplying(replying === r.id ? null : r.id)}
                  >
                    Reply to the visitor
                  </button>
                )}
              </div>
            )}
            {canAct && r.post && (onPost.get(r.post.id) ?? 0) > 1 && (
              <span className="small muted">
                {onPost.get(r.post.id)} visitors reported this post. Keeping or removing it answers
                all of them.
              </span>
            )}
            {r.reason === "privacy" && canAct && (
              <span className="small muted">
                Removing it also counts as not approved: its saved files are deleted.
              </span>
            )}
            {replying === r.id && (
              <form
                className="stack"
                style={{ gap: 6 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (text.trim()) act(r.id, "reply", text);
                }}
              >
                <textarea
                  className="input"
                  rows={3}
                  value={text}
                  aria-label="Your reply"
                  onChange={(e) => setText(e.target.value)}
                />
                <button type="submit" className="btn sm" style={{ justifySelf: "start" }}>
                  Send reply
                </button>
              </form>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
